import type { FlexProps } from '@chakra-ui/react';
import { Box, Flex, HStack, Text } from '@chakra-ui/react';
import { InDesktop, InMobile } from '@cronos-app/components';
import { currentWallet } from '@cronos-app/wallet';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import React, { forwardRef } from 'react';

export type DisplayCardProps = FlexProps & {
  balance?: ReactNode;
  usd?: ReactNode;
  inputChips?: ReactNode;
  error?: ReactNode;
  warn?: ReactNode;
  chainSelector?: ReactNode;
  tokenSelector?: ReactNode;
};

export const DisplayCard = forwardRef<HTMLDivElement, DisplayCardProps>(
  (
    {
      children,
      balance,
      usd,
      inputChips,
      error,
      warn,
      chainSelector,
      tokenSelector,
      ...props
    },
    ref,
  ) => {
    const t = useTranslations();
    const isConnected = currentWallet.useIsConnected();
    const hasRightElements = !!inputChips;

    return (
      <Box
        className={[
          'cronos-display-card',
          error ? 'cronos-display-card--error' : '',
        ].join(' ')}
        {...props}
      >
        {chainSelector && (
          <Box
            display="inline-flex"
            minW={{
              base: '140px',
              desktop: '275px',
            }}
            mb="-6px"
          >
            {chainSelector}
          </Box>
        )}
        <Flex alignItems="stretch">
          <Flex
            ref={ref}
            flex="1"
            flexDir="column"
            minH="92px"
            bg="gradients.dark-blue"
            px={{
              base: '16px',
              desktop: '24px',
            }}
            py="8px"
            borderWidth="1px"
            borderColor={error ? 'brand.error' : 'brand.gray-scale-border-20'}
            borderRightColor={
              error && tokenSelector ? 'transparent' : undefined
            }
            roundedLeft="8px"
            roundedRight={tokenSelector ? '0px' : '8px'}
          >
            {balance && (
              <Flex
                opacity={isConnected ? 1 : 0}
                justify="space-between"
                alignItems="center"
                gap="8px"
                wordBreak="break-word"
              >
                <Text
                  color="brand.gray-scale-light-grey"
                  textStyle={{
                    base: 'caption',
                    desktop: 'body2',
                  }}
                  textTransform="capitalize"
                >
                  {t('card.balance')}
                </Text>
                <Box
                  color="brand.white"
                  textStyle={{
                    base: 'caption',
                    desktop: 'body2Bold',
                  }}
                >
                  {balance}
                </Box>
              </Flex>
            )}
            <Box
              textStyle={{
                base: 'subtitle1',
                desktop: 'h3',
              }}
            >
              {children}
            </Box>
            <HStack>
              <Box
                w={{
                  desktop: hasRightElements ? '55%' : 'auto',
                }}
                wordBreak="break-word"
              >
                {!error && usd && (
                  <Box textStyle="body2" color="brand.white">
                    {usd}
                  </Box>
                )}
                {error && (
                  <Box
                    textStyle="body2"
                    color="brand.error"
                    _firstLetter={{
                      textTransform: 'capitalize',
                    }}
                  >
                    {error}
                  </Box>
                )}
                {warn && (
                  <Box
                    textStyle="overline"
                    color="brand.warn"
                    _firstLetter={{
                      textTransform: 'capitalize',
                    }}
                  >
                    {warn}
                  </Box>
                )}
              </Box>
              {inputChips && (
                <InDesktop>
                  <Box
                    // In case the inputChips element is overlapped by the input element
                    position="relative"
                    zIndex="1"
                    flexShrink="0"
                  >
                    {inputChips}
                  </Box>
                </InDesktop>
              )}
            </HStack>
          </Flex>
          {tokenSelector && (
            <Flex
              w={{
                base: '110px',
                desktop: '180px',
              }}
              flexShrink="0"
              alignItems="stretch"
              justify="stretch"
              ml="-1px"
            >
              {tokenSelector}
            </Flex>
          )}
        </Flex>
        {inputChips && (
          <InMobile>
            <Box mt="8px">{inputChips}</Box>
          </InMobile>
        )}
      </Box>
    );
  },
);

DisplayCard.displayName = 'DisplayCard';
